import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft, FaPaperPlane, FaPlus, FaUser, FaFileInvoiceDollar,
  FaMoneyCheckAlt, FaCreditCard, FaExternalLinkAlt, FaTrash, FaReceipt,
  FaMapMarkerAlt, FaEnvelope, FaPhone, FaCheckCircle, FaClock, FaFileSignature, FaLink, FaEdit
} from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import { fetchInvoiceById, sendInvoice, sendReceipt, deletePayment, deleteInvoice, updateInvoice } from './financialService';
import { formatCurrency } from '../../utils/utils';
import InfoCard from '../../components/shared/InfoCard';
import { DetailField, DetailGrid, DetailHeader, DetailTitle, ActionBar } from '../../components/styled/DetailComponents';
import { ActionButton, StatusBadge } from '../../components/styled/CommonStyles';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import Modal from '../../components/shared/Modal';
import PaymentForm from './PaymentForm';
import BillToModal from './BillToModal';

const BalanceCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const BalanceCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  text-align: center;
  border-top: 3px solid ${props => props.accent || props.theme.colors.primary};
  .label { font-size: 11px; color: ${props => props.theme.colors.textSecondary}; text-transform: uppercase; margin-bottom: 4px; }
  .value { font-size: 20px; font-weight: bold; color: ${props => props.valueColor || props.theme.colors.textPrimary}; }
`;

const ScheduleItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing(2)};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  margin-bottom: ${props => props.theme.spacing(1)};
  border-left: 3px solid ${props => props.paid ? props.theme.colors.success : '#F69620'};
  flex-wrap: wrap;
  gap: 8px;
`;

const PaymentCard = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(1)};
  border-left: 3px solid ${props => props.theme.colors.success};
`;

const PaymentCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const PaymentCardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: ${props => props.theme.spacing(1)};
`;

const ServiceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing(2)};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  margin-bottom: ${props => props.theme.spacing(1)};
  flex-wrap: wrap;
  gap: 6px;
`;

const INVOICE_STATUSES = ['Draft', 'Sent', 'Partially Paid', 'Paid', 'Overdue', 'Cancelled'];

const getStatusColor = (status) => {
  const map = {
    'Draft': '#6c757d', 'Sent': '#007bff', 'Partially Paid': '#F69620',
    'Paid': '#28a745', 'Overdue': '#dc3545', 'Cancelled': '#6c757d'
  };
  return map[status] || '#6c757d';
};

const StatusSelect = styled.select`
  padding: 4px 8px;
  border-radius: 6px;
  border: 1.5px solid ${(props) => props.borderColor || '#6c757d'};
  background: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.textPrimary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  &:focus { border-color: ${(props) => props.theme.colors.primary}; }
`;

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [deletePaymentModalOpen, setDeletePaymentModalOpen] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [deleteInvoiceModalOpen, setDeleteInvoiceModalOpen] = useState(false);
  const [billToModalOpen, setBillToModalOpen] = useState(false);

  useEffect(() => { loadInvoice(); }, [id]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const data = await fetchInvoiceById(id);
      setInvoice(data);
    } catch (error) {
      console.error('Error loading invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      await sendInvoice(id);
      setSendModalOpen(false);
      feedback(true, 'Invoice sent successfully!');
      loadInvoice();
    } catch (error) {
      setSendModalOpen(false);
      feedback(false, error.response?.data?.error || 'Failed to send invoice');
    }
  };

  const handleSendReceipt = async (paymentId) => {
    try {
      await sendReceipt(id, paymentId);
      feedback(true, 'Receipt sent successfully!');
      loadInvoice();
    } catch (error) {
      feedback(false, error.response?.data?.error || 'Failed to send receipt');
    }
  };

  const handleDeletePayment = async () => {
    if (!selectedPaymentId) return;
    try {
      await deletePayment(id, selectedPaymentId);
      setDeletePaymentModalOpen(false);
      setSelectedPaymentId(null);
      feedback(true, 'Payment deleted');
      loadInvoice();
    } catch (error) {
      setDeletePaymentModalOpen(false);
      setSelectedPaymentId(null);
      feedback(false, 'Failed to delete payment');
    }
  };

  const handleDeleteInvoice = async () => {
    try {
      await deleteInvoice(id);
      setDeleteInvoiceModalOpen(false);
      navigate('/financial/invoices');
    } catch (error) {
      setDeleteInvoiceModalOpen(false);
      feedback(false, error.response?.data?.error || 'Failed to delete invoice');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateInvoice(id, { status: newStatus });
      feedback(true, `Status updated to ${newStatus}`);
      loadInvoice();
    } catch (error) {
      feedback(false, error.response?.data?.error || 'Failed to update status');
    }
  };

  // Errors propagate to BillToModal, which shows them in place.
  const handleBillToSave = async ({ billToName, billToAddress }) => {
    await updateInvoice(id, { billToName, billToAddress });
    setBillToModalOpen(false);
    feedback(true, billToName ? `Invoice now billed to ${billToName}` : 'Invoice now billed to the lead');
    loadInvoice();
  };

  const feedback = (success, message) => {
    setFeedbackSuccess(success);
    setFeedbackMessage(message);
    setShowFeedback(true);
  };

  if (loading) return <DashboardLayout><LoadingSpinner /></DashboardLayout>;
  if (!invoice) return <DashboardLayout><p>Invoice not found</p></DashboardLayout>;

  const coInvoices = invoice.changeOrderInvoices || [];
  const activeCoInvoices = coInvoices.filter((ci) => ci.status !== 'Cancelled');
  const coTotalSum = activeCoInvoices.reduce((sum, ci) => sum + parseFloat(ci.totalAmount || 0), 0);
  const coPaidSum = activeCoInvoices.reduce((sum, ci) => sum + parseFloat(ci.paidAmount || 0), 0);
  const primaryTotal = parseFloat(invoice.totalAmount);
  const primaryPaid = parseFloat(invoice.paidAmount);
  const hasCoInvoices = invoice.type === 'Primary' && activeCoInvoices.length > 0;
  const combinedTotal = primaryTotal + coTotalSum;
  const combinedPaid = primaryPaid + coPaidSum;
  const displayTotal = hasCoInvoices ? combinedTotal : primaryTotal;
  const displayPaid = hasCoInvoices ? combinedPaid : primaryPaid;
  const remaining = displayTotal - displayPaid;
  // Credit change orders are already out of totalAmount. Refund wording only
  // when a credit caused the overpayment.
  const creditAmount = parseFloat(invoice.creditAmount || 0);
  const credits = invoice.credits || [];
  const refundDue = creditAmount > 0 && remaining < -0.005;
  const billedToName = invoice.billToName || invoice.Lead?.name;
  const publicUrl = invoice.publicToken ? `https://crm.mrpatchasphalt.com/invoice/${invoice.publicToken}` : null;

  return (
    <DashboardLayout>
      <DetailHeader>
        <DetailTitle>
          <ActionButton onClick={() => navigate('/financial/invoices')}>
            <FaArrowLeft />
          </ActionButton>
          <h1>{invoice.invoiceNumber}</h1>
          <StatusSelect
            value={invoice.status}
            borderColor={getStatusColor(invoice.status)}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            {INVOICE_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </StatusSelect>
          {invoice.type === 'ChangeOrder' && (
            <StatusBadge color="#17a2b8">Change Order</StatusBadge>
          )}
        </DetailTitle>
        <ActionBar>
          {(invoice.status === 'Draft' || invoice.status === 'Sent') && (
            <ActionButton variant="primary" onClick={() => setSendModalOpen(true)}>
              <FaPaperPlane /> Send Invoice
            </ActionButton>
          )}
          {invoice.status !== 'Paid' && invoice.status !== 'Cancelled' && (
            <ActionButton variant="primary" onClick={() => setShowPaymentForm(true)}>
              <FaPlus /> Record Payment
            </ActionButton>
          )}
          {publicUrl && (
            <ActionButton onClick={() => window.open(publicUrl, '_blank')}>
              <FaExternalLinkAlt /> Public Link
            </ActionButton>
          )}
          <ActionButton variant="danger" onClick={() => setDeleteInvoiceModalOpen(true)}>
            <FaTrash /> Delete
          </ActionButton>
        </ActionBar>
      </DetailHeader>

      <BalanceCards>
        <BalanceCard accent="#363636" valueColor="#363636">
          <div className="label">{hasCoInvoices ? 'Total (incl. Change Orders)' : 'Total'}</div>
          <div className="value">{formatCurrency(displayTotal)}</div>
          {hasCoInvoices && (
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              {formatCurrency(primaryTotal)} + {formatCurrency(coTotalSum)} CO
            </div>
          )}
        </BalanceCard>
        <BalanceCard accent="#28a745" valueColor="#28a745">
          <div className="label">Paid</div>
          <div className="value">{formatCurrency(displayPaid)}</div>
          {hasCoInvoices && (
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              {formatCurrency(primaryPaid)} + {formatCurrency(coPaidSum)} CO
            </div>
          )}
        </BalanceCard>
        <BalanceCard accent="#F69620" valueColor="#F69620">
          <div className="label">{refundDue ? 'Refund Due' : 'Remaining'}</div>
          <div className="value">{formatCurrency(refundDue ? -remaining : remaining)}</div>
        </BalanceCard>
      </BalanceCards>

      <InfoCard
        title="Customer"
        icon={<FaUser />}
        actions={(
          <ActionButton onClick={() => setBillToModalOpen(true)}>
            <FaEdit /> Edit Bill To
          </ActionButton>
        )}
      >
        <DetailGrid>
          <DetailField><label>{invoice.billToName ? 'Bill To' : 'Name'}</label><span>{billedToName}</span></DetailField>
          {invoice.billToName && (
            <DetailField><label>Attn</label><span>{invoice.Lead?.name}</span></DetailField>
          )}
          <DetailField><label>Email</label><span>{invoice.Lead?.email}</span></DetailField>
          <DetailField><label>Phone</label><span>{invoice.Lead?.phone}</span></DetailField>
          {invoice.billToAddress && (
            <DetailField><label>Billing Address</label><span style={{ whiteSpace: 'pre-line' }}>{invoice.billToAddress}</span></DetailField>
          )}
          <DetailField><label>{invoice.billToAddress ? 'Service Address' : 'Address'}</label><span>{(() => { const a = invoice.Estimate?.leadAddress || invoice.Lead; return [a?.street, a?.city, a?.state, a?.zipcode].filter(Boolean).join(', '); })()}</span></DetailField>
          <DetailField><label>Estimate</label><span>Quote #{invoice.estimateId}</span></DetailField>
          <DetailField><label>Created</label><span>{new Date(invoice.createdAt).toLocaleDateString()}</span></DetailField>
        </DetailGrid>
      </InfoCard>

      {invoice.type === 'ChangeOrder' && invoice.parentInvoice && (
        <InfoCard title="Part of Primary Invoice" icon={<FaLink />}>
          <div
            style={{ cursor: 'pointer', color: '#007bff', display: 'flex', alignItems: 'center', gap: 8 }}
            onClick={() => navigate(`/financial/invoices/${invoice.parentInvoice.id}`)}
          >
            <FaFileInvoiceDollar /> {invoice.parentInvoice.invoiceNumber}
            <FaExternalLinkAlt size={10} />
          </div>
        </InfoCard>
      )}

      {creditAmount > 0 && (
        <InfoCard title={`Credits Applied (${credits.length})`} icon={<FaFileSignature />}>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: 8 }}>
            Original amount {formatCurrency(primaryTotal + creditAmount)} − credits {formatCurrency(creditAmount)} = {formatCurrency(primaryTotal)}
          </div>
          {credits.map((c) => (
            <ScheduleItem key={c.id}>
              <div>
                <strong>Change Order #{c.number}</strong>
                <div style={{ fontSize: '12px', color: '#888' }}>{c.title}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <strong style={{ color: '#1b7a36' }}>{formatCurrency(c.totalPrice)}</strong>
              </div>
            </ScheduleItem>
          ))}
        </InfoCard>
      )}

      {invoice.changeOrderInvoices && invoice.changeOrderInvoices.length > 0 && (
        <InfoCard title={`Related Change Order Invoices (${invoice.changeOrderInvoices.length})`} icon={<FaFileSignature />}>
          {invoice.changeOrderInvoices.map((co) => (
            <ScheduleItem
              key={co.id}
              paid={co.status === 'Paid'}
              onClick={() => navigate(`/financial/invoices/${co.id}`)}
              style={{ cursor: 'pointer' }}
            >
              <div>
                <strong>{co.invoiceNumber}</strong>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  <StatusBadge color={getStatusColor(co.status)}>{co.status}</StatusBadge>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <strong>{formatCurrency(co.totalAmount)}</strong>
              </div>
            </ScheduleItem>
          ))}
        </InfoCard>
      )}

      <InfoCard title="Payment Schedule" icon={<FaMoneyCheckAlt />}>
        {(invoice.paymentSchedule || []).map((item, i) => (
          <ScheduleItem key={i} paid={item.isPaid}>
            <div>
              <strong>{item.label}</strong>
              <div style={{ fontSize: '12px', color: '#888' }}>{item.percentage}% · {item.dueDescription}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <strong>{formatCurrency(item.amount)}</strong>
            </div>
          </ScheduleItem>
        ))}
      </InfoCard>

      {invoice.Estimate?.items?.length > 0 && (
        <InfoCard title="Services" icon={<FaFileInvoiceDollar />}>
          {invoice.Estimate.items.map((item, i) => (
            <ServiceItem key={i}>
              <div>
                <strong>{item.Service?.service_name}</strong>
                <div style={{ fontSize: '12px', color: '#888' }}>{item.totalArea} sqft · {formatCurrency(item.pricePerSqft)}/sqft</div>
              </div>
              <strong>{formatCurrency(item.totalPrice)}</strong>
            </ServiceItem>
          ))}
        </InfoCard>
      )}

      <InfoCard title={`Payments (${invoice.payments?.length || 0})`} icon={<FaCreditCard />}>
        {invoice.payments?.length > 0 ? (
          invoice.payments.map(payment => (
            <PaymentCard key={payment.id}>
              <PaymentCardRow>
                <div>
                  <strong style={{ color: '#28a745' }}>{formatCurrency(payment.amount)}</strong>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {payment.method}
                    {payment.referenceNumber ? ` #${payment.referenceNumber}` : ''}
                    {payment.installmentLabel ? ` — ${payment.installmentLabel}` : ''}
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: '#888' }}>
                  {new Date(payment.paymentDate).toLocaleDateString()}
                  {payment.receiptSentAt && (
                    <span style={{ marginLeft: 6, color: '#28a745' }}><FaCheckCircle /> Sent</span>
                  )}
                </div>
              </PaymentCardRow>
              <PaymentCardActions>
                <ActionButton
                  onClick={() => handleSendReceipt(payment.id)}
                  title={payment.receiptSentAt ? 'Resend Receipt' : 'Send Receipt'}
                >
                  <FaReceipt /> {payment.receiptSentAt ? 'Resend' : 'Send Receipt'}
                </ActionButton>
                <ActionButton
                  variant="danger"
                  onClick={() => {
                    setSelectedPaymentId(payment.id);
                    setDeletePaymentModalOpen(true);
                  }}
                  title="Delete Payment"
                >
                  <FaTrash />
                </ActionButton>
              </PaymentCardActions>
            </PaymentCard>
          ))
        ) : (
          <p style={{ color: '#888', padding: '12px 0' }}>No payments recorded yet.</p>
        )}
      </InfoCard>

      {invoice.notes && (
        <InfoCard title="Notes" icon={<FaFileInvoiceDollar />}>
          <p style={{ color: '#555', lineHeight: 1.6 }}>{invoice.notes}</p>
        </InfoCard>
      )}

      {showPaymentForm && (
        <PaymentForm
          invoiceId={invoice.id}
          invoice={invoice}
          onClose={() => setShowPaymentForm(false)}
          onSuccess={() => {
            setShowPaymentForm(false);
            feedback(true, 'Payment recorded!');
            loadInvoice();
          }}
        />
      )}

      <Modal
        isOpen={deletePaymentModalOpen}
        title="Delete Payment"
        message="Are you sure you want to delete this payment? The invoice balance will be recalculated."
        onConfirm={handleDeletePayment}
        onCancel={() => {
          setDeletePaymentModalOpen(false);
          setSelectedPaymentId(null);
        }}
      />

      <Modal
        isOpen={sendModalOpen}
        title="Send Invoice"
        message={invoice.billToName
          ? `Send invoice ${invoice.invoiceNumber} (billed to ${invoice.billToName}) to ${invoice.Lead?.name} (${invoice.Lead?.email})?`
          : `Send invoice ${invoice.invoiceNumber} to ${invoice.Lead?.name} (${invoice.Lead?.email})?`}
        onConfirm={handleSend}
        onCancel={() => setSendModalOpen(false)}
      />

      <Modal
        isOpen={deleteInvoiceModalOpen}
        title="Delete Invoice"
        message={`Delete invoice ${invoice.invoiceNumber}? All payments and receipts will be permanently deleted. This cannot be undone.`}
        onConfirm={handleDeleteInvoice}
        onCancel={() => setDeleteInvoiceModalOpen(false)}
      />

      <BillToModal
        isOpen={billToModalOpen}
        leadName={invoice.Lead?.name}
        initial={{ billToName: invoice.billToName, billToAddress: invoice.billToAddress }}
        title="Edit Bill To"
        onSubmit={handleBillToSave}
        onCancel={() => setBillToModalOpen(false)}
      />

      <FeedbackModal
        isOpen={showFeedback}
        message={feedbackMessage}
        success={feedbackSuccess}
        onComplete={() => setShowFeedback(false)}
      />
    </DashboardLayout>
  );
};

export default InvoiceDetail;
